/**
 * Движение интерфейса по принципам Apple (WWDC 2018, «Designing Fluid Interfaces»):
 * анимация стартует с текущего значения на экране, наследует скорость пальца, проецирует
 * инерцию вперёд и в любой момент может быть перехвачена. Инструмент для всего этого —
 * спринг: у него нет фиксированной длительности, он всегда идёт «отсюда», а не «оттуда».
 *
 * Здесь только чистые функции и драйвер на `requestAnimationFrame`; React-компоненты
 * (`Sheet`) пользуются ими и ничего не считают сами.
 */

/** Параметры спринга в терминах дизайнера, а не физики (масса/жёсткость/трение). */
export interface SpringConfig {
  /** Коэффициент затухания: 1 — без перелёта (по умолчанию для UI), 0.8 — лёгкий отскок после броска. */
  damping: number;
  /** Отклик в секундах — насколько быстро значение подходит к цели. Не длительность. */
  response: number;
}

/** Спринг без перелёта — для всего, что просто открылось или переместилось. */
export const SPRING_DEFAULT: SpringConfig = { damping: 1, response: 0.35 };
/** Спринг с инерцией — только когда жест сам принёс скорость (бросок, отпущенная шторка). */
export const SPRING_MOMENTUM: SpringConfig = { damping: 0.8, response: 0.3 };

export interface SpringState {
  value: number;
  /** px/с */
  velocity: number;
}

/**
 * Куда «долетит» значение с начальной скоростью, если его отпустить (экспоненциальное
 * замедление, как у скролла). Формула из примеров Apple, не учебниковая `v²/2a`.
 * @param velocity px/с
 * @param decelerationRate 0.998 — обычный скролл, 0.99 — заметно короче
 */
export function project(velocity: number, decelerationRate = 0.998): number {
  return ((velocity / 1000) * decelerationRate) / (1 - decelerationRate);
}

/**
 * Резиновая граница: чем дальше за край, тем меньше элемент следует за пальцем.
 * Возвращает смещение, которое надо показать, при выходе за границу на `overshoot`.
 * Предел при бесконечном `overshoot` — `dimension`, знак сохраняется.
 */
export function rubberband(overshoot: number, dimension: number, constant = 0.55): number {
  if (overshoot === 0 || dimension <= 0) return 0;
  return (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot));
}

/** Ближайшая точка привязки к значению (например, «открыто» или «закрыто»). */
export function nearestSnap(value: number, points: readonly number[]): number {
  let best = points[0] ?? value;
  for (const p of points) if (Math.abs(p - value) < Math.abs(best - value)) best = p;
  return best;
}

/**
 * Один шаг спринга массой 1: жёсткость и трение выводятся из `response` и `damping`
 * (как в `UISpringTimingParameters`). Полунеявный Эйлер с подшагами — устойчив на редких кадрах.
 */
export function springStep(
  state: SpringState,
  target: number,
  config: SpringConfig,
  dtSeconds: number,
): SpringState {
  const omega = (2 * Math.PI) / Math.max(config.response, 0.01);
  const stiffness = omega * omega;
  const friction = 2 * Math.max(config.damping, 0) * omega;
  const steps = Math.max(1, Math.ceil(dtSeconds / 0.004));
  const h = dtSeconds / steps;
  let { value, velocity } = state;
  for (let i = 0; i < steps; i++) {
    const accel = -stiffness * (value - target) - friction * velocity;
    velocity += accel * h;
    value += velocity * h;
  }
  return { value, velocity };
}

/** Спринг «дошёл»: близко к цели и почти не движется. Пороги в px и px/с. */
export function springSettled(
  state: SpringState,
  target: number,
  positionEps = 0.1,
  velocityEps = 2,
): boolean {
  return Math.abs(state.value - target) < positionEps && Math.abs(state.velocity) < velocityEps;
}

/**
 * Скорость пальца по последним точкам жеста. Одна текущая точка не годится: нужна история
 * за ~100 мс, иначе случайная пауза перед отпусканием обнуляет бросок.
 */
export class VelocityTracker {
  private readonly samples: Array<{ v: number; t: number }> = [];

  constructor(private readonly windowMs = 100) {}

  push(value: number, timeMs: number): void {
    this.samples.push({ v: value, t: timeMs });
    while (this.samples.length > 1 && timeMs - (this.samples[0]?.t ?? timeMs) > this.windowMs) {
      this.samples.shift();
    }
  }

  /** px/с; 0, если истории нет или она не двигалась. */
  velocity(nowMs?: number): number {
    const first = this.samples[0];
    const last = this.samples[this.samples.length - 1];
    if (!first || !last || first === last) return 0;
    // Долгая пауза перед отпусканием — бросок кончился
    if (nowMs !== undefined && nowMs - last.t > this.windowMs) return 0;
    const dt = last.t - first.t;
    if (dt <= 0) return 0;
    return ((last.v - first.v) / dt) * 1000;
  }

  reset(): void {
    this.samples.length = 0;
  }
}

export interface SpringAnimation {
  /** Остановить и вернуть текущее значение и скорость — с них продолжает жест или новая анимация. */
  stop(): SpringState;
  readonly running: boolean;
}

export interface AnimateSpringOptions {
  from: SpringState;
  to: number;
  config?: SpringConfig;
  onUpdate: (value: number) => void;
  onComplete?: () => void;
  /** Подмена планировщика в тестах; по умолчанию `requestAnimationFrame`. */
  raf?: (cb: (t: number) => void) => number;
  cancelRaf?: (id: number) => void;
  now?: () => number;
}

/** Драйвер спринга на кадрах дисплея. Прерываем: `stop()` в любой момент отдаёт живое состояние. */
export function animateSpring(opts: AnimateSpringOptions): SpringAnimation {
  const config = opts.config ?? SPRING_DEFAULT;
  const raf = opts.raf ?? ((cb) => requestAnimationFrame(cb));
  const cancel = opts.cancelRaf ?? ((id) => cancelAnimationFrame(id));
  const now = opts.now ?? (() => performance.now());

  let state: SpringState = { ...opts.from };
  let last = now();
  let id = 0;
  let running = true;

  const frame = () => {
    if (!running) return;
    const t = now();
    // Скрытая вкладка или долгий кадр: не прыгать, а сделать один нормальный шаг
    const dt = Math.min(Math.max(t - last, 0), 64) / 1000;
    last = t;
    state = springStep(state, opts.to, config, dt);
    if (springSettled(state, opts.to)) {
      state = { value: opts.to, velocity: 0 };
      running = false;
      opts.onUpdate(state.value);
      opts.onComplete?.();
      return;
    }
    opts.onUpdate(state.value);
    id = raf(frame);
  };
  id = raf(frame);

  return {
    get running() {
      return running;
    },
    stop() {
      if (running) {
        running = false;
        cancel(id);
      }
      return { ...state };
    },
  };
}

/** Пользователь просил меньше движения: вместо сдвигов и спрингов — короткое проявление. */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}
